// Personal preferences persisted per company-user on the backend
// (company_users.preferences). The client owns the shape; the API stores it as
// an opaque JSON blob and shallow-merges partial updates.

import type { GridFilterModel, GridSortModel, GridColumnVisibilityModel, GridDensity } from '@mui/x-data-grid-pro'

// `import type` — fully erased at build, so this cannot create a runtime cycle
// between the low-level `types` barrel and the redux store. Imported rather than
// re-declared so the saved-view shape cannot drift from the live filter shape.
import type { AnalyticsDataType } from 'store/analytics/types'

/**
 * A breakpoint of the analytics dashboard grid.
 *
 * TWO, not five. Both column counts are divisible by 3, which is the whole reason
 * for the pair: a KPI tile is fixed at 3 columns (see `analyticsGrid.ts`), so at 12
 * it is a quarter of the row and at 6 it is a half. A breakpoint whose column count
 * were not a multiple of 3 would leave a KPI tile straddling a fraction of the grid
 * and a trailing gap nothing can fill.
 */
export type AnalyticsGridBreakpoint = 'lg' | 'sm'

/**
 * One card's position and size, in GRID UNITS (not pixels).
 *
 * Note what is deliberately NOT persisted: `minW` / `minH` / `maxW` / `isResizable`.
 * Those are constraints, and constraints belong to the CARD, not to the view — they
 * are supplied at render time from `analyticsGrid.ts`. Storing them would freeze a
 * card's minimum size into every saved view on the day it was saved, so tightening
 * the map's minimum later would reach new users and no existing one.
 */
export type AnalyticsGridItem = {
  /** The card id. Matches `AnalyticsCardItem.id`. */
  i: string
  x: number
  y: number
  w: number
  h: number
}

/**
 * Per-breakpoint geometry.
 *
 * PARTIAL on purpose. A breakpoint appears here only once the user has actually
 * arranged the grid at that width; the rest are generated by react-grid-layout from
 * the nearest authored one. That is not laziness — writing every generated breakpoint
 * back would make the state differ from the seed the moment the page mounted, and
 * `ViewsDropdown` compares by `stableStringify`, so the active view would silently
 * de-select itself on load.
 */
export type AnalyticsGridLayouts = Partial<Record<AnalyticsGridBreakpoint, AnalyticsGridItem[]>>


/**
 * The analytics page's dimension selections, as a saved view stores them.
 *
 * NOTE the deliberate omission: there is NO `dateRange` here, and adding one would
 * be a mistake rather than a feature. The top-level date picker is the SOLE writer
 * of the query range (the decision that killed the chart-brush coupling — a second
 * writer produced a ratchet that narrowed the range and could never widen it). A
 * saved view carrying an absolute range would be a second writer AND would go stale
 * the day after it was saved: "Aug 1 - Aug 27" is a different statement every week.
 * A view says WHAT you are looking at; the picker says WHEN.
 */
export type AnalyticsDimensionFilters = {
  ownerIds?: string[]
  productIds?: string[]
  certificateIds?: string[]
  certificateStatuses?: string[]
  /**
   * The SCAN's own outcome (VERA-260) — `verified` / `invalid` / `tampered`.
   *
   * Persisted, unlike the card-local control this replaced. That control's own note said
   * it was deliberately NOT saved because it narrowed one card's already-fetched points
   * and nothing else, and "worth revisiting if this turns out to be something people set
   * and expect to stay set". Promoting it to a real page-level dimension answers that: a
   * view is a statement of WHAT you are looking at, and "only the invalid scans" is
   * exactly that kind of statement.
   */
  scanStatuses?: string[]
  location?: string[]
  /**
   * The drawn map area (VRG-480), in the wire form `lat,lng;lat,lng;…` — SIMPLIFIED and
   * capped at 50 vertices, which is what makes persisting it affordable (a few hundred
   * bytes, comparable to a long `location` array).
   *
   * ⚠️ A STRING, not points, and that is the right shape for a persisted value: it is
   * the same text the request carries, so a view's stored filter and the query it
   * produces cannot drift into two encodings of "the same" ring. `decodeRing` turns it
   * back into points on restore, and returns null for anything malformed — a blob written
   * by an older client, or a hand-edited one — so a bad value restores NO area rather
   * than a partial one, which would narrow every figure on the page by an amount nobody
   * chose.
   *
   * The restore path must also REDRAW the shape on the map. See
   * `AnalyticsFilters.area` — a restored area with no visible shape is worse than not
   * persisting it, because the filtering becomes invisible rather than merely absent.
   */
  area?: string
  /** What the "…by time" chart counts. Omitted when it is the default (`scans`). */
  chartDataType?: AnalyticsDataType
  /** What the "…by location" map counts. Omitted when it is the default (`scans`). */
  mapDataType?: AnalyticsDataType
  /**
   * @deprecated The single page-level dimension, replaced by the two per-panel keys
   * above (see `AnalyticsFilters` in `store/analytics/types` for why it was split).
   *
   * ⚠️ READ-ONLY, and it must stay readable. Saved views live in user preferences as
   * opaque jsonb, so every view saved before the split still carries this key and
   * nothing migrates it — dropping the field would silently downgrade those views to
   * the default dimension. `useAnalyticsView` reads it as the fallback for BOTH panels,
   * which reproduces exactly what the view meant when it was saved. Nothing writes it.
   */
  dataType?: AnalyticsDataType
}

/**
 * The analytics page's slot inside a saved view.
 *
 * OPTIONAL and ADDITIVE by design: `TableViewState` is otherwise a pure DataGrid
 * shape, and every existing table view omits this key entirely, so no existing view
 * changes behaviour. `ViewsDropdown` never inspects the shape — it only
 * `stableStringify`s it to compare — and persistence (`useTableViews` -> user
 * preferences -> jsonb) is likewise shape-agnostic. That is why the analytics page
 * can reuse the whole saved-view machinery instead of growing a parallel one.
 *
 * What is here is what a person means by "my view of analytics": which cards, in
 * what order, laid out how, filtered by what. What is NOT here is ephemeral UI
 * state — notably which card is EXPANDED. Restoring a view must never reopen
 * someone inside a maximized card.
 */
export type AnalyticsViewState = {
  /**
   * Which cards are on the canvas. MEMBERSHIP only — position lives in `layouts`.
   *
   * Two fields rather than one because they answer to different lifetimes.
   * Membership is breakpoint-independent (a card you removed is removed at every
   * width), while geometry is not. Deriving membership from `layouts` instead would
   * make "is this card shown" a question with one answer per breakpoint, and adding
   * a card at 1280 would leave it missing at 900.
   *
   * The order is also the fallback: a card with no entry in `layouts` is placed by
   * `analyticsGrid.ts` in this order, which is what lets a seed author geometry for
   * `lg` alone and still get a sane `sm`.
   */
  cards?: string[]
  /** Per-breakpoint geometry. Partial — see `AnalyticsGridLayouts`. */
  layouts?: AnalyticsGridLayouts
  /** Dimension selections. Absent keys read as "nothing selected". */
  filters?: AnalyticsDimensionFilters
}

// Serializable snapshot of a table's filter + sort + view state.
export type TableViewState = {
  filterModel?: GridFilterModel
  sortModel?: GridSortModel
  columnVisibilityModel?: GridColumnVisibilityModel
  density?: GridDensity
  /**
   * Present only for surfaces that are not DataGrids — today the Analytics page.
   * See `AnalyticsViewState`. Optional so every table view is unaffected.
   */
  analytics?: AnalyticsViewState
}

// A named, per-user saved custom view (Jira-like saved filter).
export type SavedTableView = {
  id: string
  name: string
  state: TableViewState
  createdAt: string
}

export type UserPreferences = {
  // When true, the app persists the current filters/sort/view per table and
  // restores them on next login.
  saveStateOnLogin?: boolean
  // Named saved views, keyed by table viewKey.
  savedViews?: Record<string, SavedTableView[]>
  // The currently-applied saved view per table (highlighted in the UI).
  activeViewId?: Record<string, string | null>
  // Last-used view state per table, restored on next login when enabled.
  lastViewState?: Record<string, TableViewState>
  /**
   * VRG-338: the setup checklist is COLLAPSED to its header for this user.
   *
   * Per-USER on purpose — checklist completion is company-scoped and retires the block for
   * everyone at once, but one admin tidying their own Home must not collapse it for a new
   * hire. Server-side rather than localStorage so it follows the person across devices.
   *
   * REPLACED `homeDismissed`, which destroyed the block permanently with no way back from the
   * UI. That needed a confirmation modal AND a restore affordance to be safe — three pieces of
   * machinery to make an irreversible action survivable. Collapsing needs none of them,
   * because nothing is lost: the header stays, the count stays, and expanding is one click.
   */
  homeChecklistCollapsed?: boolean
  /**
   * @deprecated Read-only, for back-compat. Anyone who dismissed under the old model is
   * treated as collapsed — the block returns as a header rather than reappearing in full,
   * which is closest to the state they chose.
   */
  homeDismissed?: boolean
}

export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  saveStateOnLogin: false,
  savedViews: {},
  activeViewId: {},
  lastViewState: {},
  homeChecklistCollapsed: false,
}
